/** Source-control connections and Bitrise provisioning. */
import { z } from 'zod';
import { uuid } from './common.js';
import { CredentialKind, isProviderTokenKind } from './credentials.js';

/** Source-control providers Hangar connects to (SPEC §37; Bitbucket first). */
export const ScmProvider = z.enum(['bitbucket']);
export type ScmProvider = z.infer<typeof ScmProvider>;
/** The credential kind an SCM connection's `credentialId` must reference: a workspace or repository access token (Bearer). */
export const SCM_TOKEN_KIND = { bitbucket: 'bitbucket_access_token' } as const satisfies Record<ScmProvider, CredentialKind>;
/** Every kind Hangar stores itself as an envelope-encrypted token: provider tokens and source-control tokens. */
export const isStoredTokenKind = (kind: string): boolean => isProviderTokenKind(kind) || Object.values(SCM_TOKEN_KIND).includes(kind as never);

/** Bitbucket workspace and repository slugs (they go into API paths). */
export const BITBUCKET_SLUG = /^[A-Za-z0-9][A-Za-z0-9._~-]{0,127}$/;
const bitbucketSlug = z.string().regex(BITBUCKET_SLUG, 'letters, digits, ".", "_", "~" and "-", starting with a letter or a digit');

/** `POST /scm-connections`: a Bitbucket workspace and the token Hangar reaches it with. */
export const ScmConnectionCreateRequest = z.object({
  provider: ScmProvider.default('bitbucket'),
  name: z.string().trim().min(1).max(120),
  /** The workspace slug (the part after bitbucket.org/ in its URL). */
  workspace: bitbucketSlug,
  /**
   * The repository slug when the token is a repository access token (one repository, any Bitbucket plan; plan P34).
   * Omitted for a workspace or project access token (Bitbucket Premium), which reaches every repository it covers.
   */
  repository: bitbucketSlug.optional(),
  credentialId: uuid,
});
export type ScmConnectionCreateRequest = z.infer<typeof ScmConnectionCreateRequest>;

/**
 * `POST /scm-connections/verify`: checks a token before it is stored (plan P34): accepted, can read the repository (or list
 * the workspace), may manage webhooks. Nothing is stored, logged or audited with the token.
 */
export const ScmTokenVerifyRequest = z.object({
  provider: ScmProvider.default('bitbucket'),
  workspace: bitbucketSlug,
  repository: bitbucketSlug.optional(),
  token: z.string().trim().min(20).max(4096),
});
export type ScmTokenVerifyRequest = z.infer<typeof ScmTokenVerifyRequest>;

/** `POST /scm-connections/discover`: what a token reaches, before anyone types a workspace or repository (plan P34). */
export const ScmTokenDiscoverRequest = z.object({
  provider: ScmProvider.default('bitbucket'),
  token: z.string().trim().min(20).max(4096),
});
export type ScmTokenDiscoverRequest = z.infer<typeof ScmTokenDiscoverRequest>;

export const ScmConnectionPatch = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  credentialId: uuid.optional(),
}).strict();
export type ScmConnectionPatch = z.infer<typeof ScmConnectionPatch>;

/** `POST /apps/{appId}/bitrise/provision`: create (or reuse) the Bitrise app of an app's repository. */
export const BitriseProvisionRequest = z.object({
  /** The SCM connection (Bitbucket workspace) the repository is in. */
  scmConnectionId: uuid,
  /** The repository slug inside the connection's workspace. */
  repository: bitbucketSlug,
  /** The Bitrise provider account (workspace and token); optional when the org has exactly one. */
  providerAccountId: uuid.optional(),
  /**
   * What to do when Bitrise already has an app for this repository: `reuse` it (`slug` required) or `create` another.
   * Omitted: the request answers 409 with the existing apps, so the person chooses.
   */
  bitriseApp: z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('create') }),
    z.object({ mode: z.literal('reuse'), slug: z.string().min(1).max(100) }),
  ]).optional(),
});
export type BitriseProvisionRequest = z.infer<typeof BitriseProvisionRequest>;
